// Global Instructions Rule Applied!

import {
  SET_INVESTOR_STATEMENT_RUNS,
  APPEND_INVESTOR_STATEMENT_RUNS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  UPDATE_INVESTOR_STATEMENT_RUN_RECORD,
  REMOVE_INVESTOR_STATEMENT_RUN
} from './constants.ts'
import { InvestorStatementRunRecord, InvestorStatementRunRecordFields } from '../../data_models/investor_statement_run_record.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface InvestorStatementRunsPaginatedResponse {
  data: InvestorStatementRunRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface InvestorStatementRunsState {
  data: InvestorStatementRunRecord[]
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type InvestorStatementRunsAction =
  | { type: typeof SET_INVESTOR_STATEMENT_RUNS; payload: { response: InvestorStatementRunsPaginatedResponse } }
  | { type: typeof APPEND_INVESTOR_STATEMENT_RUNS; payload: { response: InvestorStatementRunsPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof CLEAR_ACTIVE_FILTERS }
  | { type: typeof UPDATE_INVESTOR_STATEMENT_RUN_RECORD; payload: { runId: string; updates: Record<string, any> } }
  | { type: typeof REMOVE_INVESTOR_STATEMENT_RUN; payload: { runId: string } }

const investorStatementRunsReducer = (
  state: InvestorStatementRunsState = {
    data: [],
    activeFilters: [],
    activeSorting: [],
    hasMore: false,
    currentPage: 1
  },
  action: InvestorStatementRunsAction
): InvestorStatementRunsState => {
  const idKey = InvestorStatementRunRecordFields.investor_statement_runs__id
  switch (action.type) {
    case SET_INVESTOR_STATEMENT_RUNS: {
      const { response } = action.payload
      return { ...state, data: response.data, hasMore: response.hasMore, currentPage: 1 }
    }

    case APPEND_INVESTOR_STATEMENT_RUNS: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }

    case SET_ACTIVE_FILTERS: {
      return { ...state, activeFilters: action.payload.filters }
    }

    case SET_ACTIVE_SORTING: {
      return { ...state, activeSorting: action.payload.sorting }
    }

    case CLEAR_ACTIVE_FILTERS: {
      return { ...state, activeFilters: [] }
    }

    case UPDATE_INVESTOR_STATEMENT_RUN_RECORD: {
      const { runId, updates } = action.payload
      if (!state.data.some((r) => (r as any)[idKey] === runId)) return state
      return {
        ...state,
        data: state.data.map((r) => ((r as any)[idKey] === runId ? { ...r, ...updates } : r))
      }
    }

    case REMOVE_INVESTOR_STATEMENT_RUN: {
      const { runId } = action.payload
      return { ...state, data: state.data.filter((r) => (r as any)[idKey] !== runId) }
    }

    default:
      return state
  }
}

export default investorStatementRunsReducer

export const setInvestorStatementRuns = (payload: { response: InvestorStatementRunsPaginatedResponse }) => ({
  type: SET_INVESTOR_STATEMENT_RUNS,
  payload
})

export const appendInvestorStatementRuns = (payload: { response: InvestorStatementRunsPaginatedResponse }) => ({
  type: APPEND_INVESTOR_STATEMENT_RUNS,
  payload
})

export const setInvestorStatementRunsActiveFilters = (payload: { filters: FilterValue[] }) => ({
  type: SET_ACTIVE_FILTERS,
  payload
})

export const setInvestorStatementRunsActiveSorting = (payload: { sorting: SortConfig[] }) => ({
  type: SET_ACTIVE_SORTING,
  payload
})

export const clearInvestorStatementRunsActiveFilters = () => ({ type: CLEAR_ACTIVE_FILTERS })

export const updateInvestorStatementRunRecord = (payload: { runId: string; updates: Record<string, any> }) => ({
  type: UPDATE_INVESTOR_STATEMENT_RUN_RECORD,
  payload
})

export const removeInvestorStatementRun = (payload: { runId: string }) => ({
  type: REMOVE_INVESTOR_STATEMENT_RUN,
  payload
})

export const selectInvestorStatementRunById = (state: any, runId: string) =>
  (state.investorStatementRuns?.data ?? []).find(
    (r: any) => r[InvestorStatementRunRecordFields.investor_statement_runs__id] === runId
  )
